/**
 * Client-side PDF export for the entity performance report (option 1B).
 *
 * `useReportPdf` produces a **vector** `@react-pdf/renderer` document — crisp
 * title, period line, and KPI cards laid out to match the on-screen report —
 * with each chart embedded as a **high-resolution PNG** rasterised from its live
 * Recharts SVG (see {@link rasterizeCharts}). This is the faithful-replica
 * approach: charts are pixel-identical to screen while the surrounding document
 * text stays sharp and selectable, and the file is small. It is entirely
 * frontend — no server round-trip.
 *
 * On download the hook: rasterises the charts inside the report container,
 * groups those images into the document's graph sections by their section tag,
 * renders {@link ReportPdfDocument} to a Blob via `pdf(...).toBlob()`, and
 * triggers an automatic download with the spec filename
 * `[entity]_[start]_[frequency].pdf`.
 *
 * The container ref points at the real report node (inside the section's shadow
 * root); {@link rasterizeCharts} inlines computed styles so the serialised chart
 * SVGs paint correctly outside that shadow tree.
 */
import { useCallback, useState } from "react"
import { pdf } from "@react-pdf/renderer"
import { format } from "date-fns"
import type { Cadence } from "./periodOptions"
import { rasterizeCharts } from "./rasterizeCharts"
import ReportPdfDocument, { type ReportPdfData, type ReportPdfSection } from "./ReportPdfDocument"

export interface ReportPdfMeta {
  /** Entity name used as the filename stem (e.g. "Garic SES"). */
  entityName?: string
  /** ISO 8601 window start of the selected period — the filename's date token. */
  start?: string
  /**
   * Report cadence, driving the filename's granularity token: `weekly` → "week",
   * `monthly` → "month", `daily` → no token (omitted). See
   * {@link buildReportPdfFilename}. This is the *cadence* (the period selector),
   * not the graph bucket `frequency` — weekly and monthly share a daily bucket,
   * so the bucket can't distinguish them.
   */
  cadence?: Cadence
}

/**
 * The document content the caller supplies, minus the chart images (which the
 * hook rasterises from the DOM). `sectionOrder` lists the graph sections in
 * document order; the hook fills each with the charts tagged for it.
 */
export interface ReportPdfContent {
  /** Header title (e.g. "Garic SES performance report"). */
  title: string
  /** Period metadata line (cadence · range · issued). */
  periodLine: string
  /** Diesel-baseline caption. */
  caption: string
  /** Headline KPI cards, pre-formatted. */
  kpis: ReportPdfData["kpis"]
  /** Graph section titles in document order (charts are matched in by section). */
  sectionOrder: string[]
}

export interface UseReportPdfResult {
  /** Rasterise, build, and download the PDF. Resolves when the save fires. */
  download: () => Promise<void>
  /** True while a rasterise/render is in flight (drives the button busy state). */
  generating: boolean
  /** A normalised `Error` if the last attempt failed, otherwise `undefined`. */
  error?: Error
}

/**
 * Sanitise a string for use in a filename: collapse whitespace to single
 * underscores and strip characters that are illegal or awkward across operating
 * systems (`/ \ : * ? " < > |` and control chars). Bracketed customer prefixes
 * like "[Garic]" are kept, so "[Garic] SES" → "[Garic]_SES".
 */
function sanitiseFilenamePart(value: string): string {
  return value
    .trim()
    // eslint-disable-next-line no-control-regex -- strip ASCII control chars
    .replace(/[\u0000-\u001f/\\:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
}

/**
 * Map a report cadence to its filename granularity token. Daily is the implicit
 * default and gets *no* token; weekly/monthly are tagged "week"/"month" so a
 * download reads e.g. "Garic_SES_2026-09-08_week.pdf".
 */
const CADENCE_FILENAME_TOKEN: Record<Cadence, string> = {
  daily: "",
  weekly: "week",
  monthly: "month",
}

/**
 * Build the download filename: `[entity]_[start yyyy-MM-dd](_[granularity]).pdf`.
 *
 * Each part falls back gracefully so a missing input never yields a broken name:
 * a missing entity name → "report"; a missing/invalid start → today's date. The
 * granularity token comes from the cadence (weekly → "week", monthly → "month",
 * daily → omitted) rather than the graph bucket. Examples:
 * "[Garic]_SES_2026-09-08_week.pdf" (weekly), "SES_2026-09-08.pdf" (daily).
 */
export function buildReportPdfFilename(meta: ReportPdfMeta): string {
  const namePart = sanitiseFilenamePart(meta.entityName?.trim() || "report")

  let datePart: string
  try {
    datePart = format(meta.start ? new Date(meta.start) : new Date(), "yyyy-MM-dd")
  } catch {
    datePart = format(new Date(), "yyyy-MM-dd")
  }

  const parts = [namePart, datePart]
  const cadenceToken = meta.cadence ? CADENCE_FILENAME_TOKEN[meta.cadence] : ""
  if (cadenceToken) parts.push(cadenceToken)
  return `${parts.join("_")}.pdf`
}

/**
 * Trigger a browser download of `blob` under `filename` via a transient anchor.
 * The object URL is revoked on the next tick so the download isn't cancelled.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  anchor.rel = "noopener"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * Provide a `download` action that exports the report to a vector PDF (with
 * rasterised charts) and saves it, plus `generating` / `error` state for the UI.
 *
 * @param containerRef Ref to the report surface node whose charts to capture.
 *                     When null (not yet mounted) `download` is a no-op.
 * @param meta         Filename inputs (entity name, period start, cadence).
 * @param content      Document content minus charts (title, period, KPIs, and
 *                     the ordered graph-section titles the charts slot into).
 */
export function useReportPdf(
  containerRef: React.RefObject<HTMLElement | null>,
  meta: ReportPdfMeta,
  content: ReportPdfContent,
): UseReportPdfResult {
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)

  const download = useCallback(async () => {
    const node = containerRef.current
    if (!node) return

    setGenerating(true)
    setError(undefined)
    try {
      // Rasterise the live charts, then group them into the document's graph
      // sections by their `section` tag, preserving the caller's section order.
      const charts = await rasterizeCharts(node)
      const sections: ReportPdfSection[] = content.sectionOrder.map((title) => ({
        title,
        charts: charts.filter((chart) => chart.section === title),
      }))

      const data: ReportPdfData = {
        title: content.title,
        periodLine: content.periodLine,
        caption: content.caption,
        kpis: content.kpis,
        sections,
      }

      const blob = await pdf(<ReportPdfDocument data={data} />).toBlob()
      saveBlob(blob, buildReportPdfFilename(meta))
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error("Failed to generate PDF"))
    } finally {
      setGenerating(false)
    }
  }, [containerRef, meta, content])

  return { download, generating, error }
}

export default useReportPdf
